import { useId, useState } from 'react';
import { PageHeader } from '@hana/shell';
import { Button, Chip, Modal, Skeleton, StateView, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import './docs.i18n';
import { StateCard, meeStateProps } from '../meeState';
import { IconAlarm, IconCircleOff, IconFileText, IconLock, IconMapPin, IconRefresh, IconSend, IconUpload } from '@tabler/icons-react';
import { MeeHeaderTools } from '../Parts';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import { fdw } from '../home/view';
import { CONSENT_FILE, setConsent, useLocationConsent } from '../checkin/fieldLocation';
import { PAPER_KINDS, SECRETS, contractsOf, kindFor, papersOf, type Contract } from './papers';

const RING_R = 18;
const RING_LEN = 2 * Math.PI * RING_R;
const CONTRACT_CHIP: Record<Contract['state'], { tone: 'ok' | 'warn' | 'neutral'; label: string }> = {
  ok: { tone: 'ok', label: 'Hiệu lực' },
  sign: { tone: 'warn', label: 'Chờ ký' },
  end: { tone: 'neutral', label: 'Hết hiệu lực' },
};

function DocsBody({ m, signed, onSign }: { m: ReturnType<typeof useMee>; signed: boolean; onSign: () => void }) {
  const t = useT();
  const toast = useToast();
  const me = m.me!;
  const [paper, setPaper] = useState<{ k?: string } | null>(null);
  const rid = useId();
  const contracts = contractsOf(me.id, signed);
  const papers = papersOf(me.id, m.today);
  const kind = kindFor(paper?.k);
  const loc = useLocationConsent();
  const basic = [
    { k: 'Ngày vào', v: fdw(me.joined).slice(3) },
    { k: 'Phòng', v: me.dept },
    { k: 'Chức danh', v: me.seats[0]![1] },
    { k: 'Quản lý trực tiếp', v: `[${me.route[0]![0]}]` },
  ];
  const open = (c: Contract) =>
    c.state === 'sign'
      ? (onSign(), toast({ msg: `Đã ký ${c.code.split(' ·')[0]} bằng passkey · gửi HR` }))
      : toast({ msg: `Mở ${c.code.split(' ·')[0]} trong trình xem tệp` });

  return (
    <div className={`${s.page} ${s.docsGrid}`}>
      <section className={`${s.card} ${s.docsMe}`} aria-label="Thông tin cơ bản">
        <div className={s.docsWho}>
          <span className={s.docsAv} aria-hidden="true">{me.initials}</span>
          <div>
            <b>{me.name}</b>
            <span>{me.id} · {me.dept}</span>
          </div>
        </div>
        <div className={s.meta}>
          {me.seats.map(([c, n, ty]) => (
            <Chip key={c} tone={ty === 'Primary' ? 'info' : 'warn'} label={`${n} · ${ty}`}>[{c}] {ty}</Chip>
          ))}
        </div>
        <div className={s.docsBasic}>
          {basic.map((x) => (
            <div key={x.k}>
              <span>{x.k}</span>
              <b>{x.v}</b>
            </div>
          ))}
        </div>
        <div className={s.docsSecret}>
          {SECRETS.map((x) => (
            <div key={x} title="Dữ liệu nhạy cảm · chỉ hiện trên máy của chính chủ sau khi xác thực passkey">
              <span>{x}</span>
              <i>••••••</i>
              <IconLock size={14} stroke={2} aria-hidden="true" />
            </div>
          ))}
        </div>
        <Button icon={<IconRefresh size={16} />} onClick={() => toast({ msg: 'Đã gửi yêu cầu cập nhật hồ sơ tới [HRD-MGR] HANHLTM' })}>
          {t('dc.reqUpdate')}
        </Button>
      </section>
      <div className={s.col}>
        <section className={s.card} aria-label={t('dc.contract')}>
          <div className={s.head}><b>{t('dc.contract')}</b></div>
          <div className={s.list}>
            {contracts.map((c) => (
              <div key={c.k} className={s.item}>
                <span className={s.ic} data-tone="pri"><IconFileText size={16} stroke={2} /></span>
                <span className={s.main} title={`${c.k} · xem bằng FilePreview`}>
                  <b>{c.k}</b>
                  <span>{c.code}</span>
                </span>
                <Chip tone={CONTRACT_CHIP[c.state].tone}>{CONTRACT_CHIP[c.state].label}</Chip>
                <Button size="sm" variant={c.state === 'sign' ? 'primary' : 'secondary'} onClick={() => open(c)}>
                  {c.state === 'sign' ? 'Ký' : 'Xem'}
                </Button>
              </div>
            ))}
          </div>
        </section>
        <section className={s.card} aria-label={t('dc.locConsent')}>
          <div className={s.head}><b>{t('dc.locConsent')}</b></div>
          <div className={s.list}>
            <div className={s.item}>
              <span className={s.ic} data-tone="pri"><IconMapPin size={16} stroke={2} /></span>
              <span className={s.main} title={t('dc.locHint')}>
                <b>{loc.agreed ? t('dc.locOn') : t('dc.locOff')}</b>
                <span>{loc.agreed ? `${fdw(loc.signedOn!).slice(3)} · ${loc.file}` : t('dc.locHint')}</span>
              </span>
              <Chip tone={loc.agreed ? 'ok' : 'neutral'}>{loc.agreed ? t('dc.locAgreed') : t('dc.locNone')}</Chip>
              <Button
                size="sm"
                variant={loc.agreed ? 'secondary' : 'primary'}
                onClick={() => (setConsent(!loc.agreed, m.today), toast({ msg: loc.agreed ? t('dc.locRevoked') : t('dc.locSigned', { f: CONSENT_FILE }), undo: () => setConsent(loc.agreed, m.today) }))}
              >
                {loc.agreed ? t('dc.locRevoke') : t('dc.locAgree')}
              </Button>
            </div>
          </div>
        </section>
        <section className={s.card} aria-label={t('dc.expiring')}>
          <div className={s.head}>
            <b>{t('dc.expiring')}</b>
            <span className={s.info} title="Nhắc gia hạn 30 ngày trước hạn" aria-label="Nhắc gia hạn 30 ngày trước hạn">ⓘ</span>
          </div>
          <div className={s.papers}>
            {papers.map((p) => (
              <div key={p.k} className={s.paper}>
                <svg aria-hidden="true" width="40" height="40" viewBox="0 0 44 44">
                  <circle cx="22" cy="22" r={RING_R} fill="none" stroke="var(--line)" strokeWidth="5" />
                  <circle
                    cx="22" cy="22" r={RING_R} fill="none" strokeWidth="5" strokeLinecap="round" transform="rotate(-90 22 22)"
                    stroke={p.tone === 'err' ? 'var(--err)' : p.tone === 'warn' ? 'var(--warn)' : 'var(--ok)'}
                    strokeDasharray={`${(p.ring * RING_LEN).toFixed(1)} ${RING_LEN.toFixed(1)}`}
                  />
                </svg>
                <span className={s.main}>
                  <b>{p.k}</b>
                  <span>{p.exp}</span>
                </span>
                <Chip tone={p.tone} icon={p.soon ? <IconAlarm size={12} /> : undefined} label={p.soon ? `${p.badge} · Đã nhắc gia hạn` : p.badge}>{p.badge}</Chip>
                {p.soon && (
                  <Button size="sm" variant="ghost" icon={<IconUpload size={15} />} aria-label={`Tải bản gia hạn ${p.k}`} onClick={() => setPaper({ k: p.k })} />
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
      <Modal
        open={!!paper}
        onOpenChange={(o) => !o && setPaper(null)}
        title={paper?.k ? `Gia hạn · ${paper.k}` : 'Thêm giấy tờ'}
        footer={
          <>
            <Button variant="ghost" icon={<IconCircleOff size={16} />} onClick={() => setPaper(null)}>{t('dc.cancel')}</Button>
            <Button
              variant="primary"
              icon={<IconSend size={16} />}
              onClick={() => (setPaper(null), toast({ msg: 'Đã gửi bản scan · HR xác nhận rồi cập nhật hạn' }))}
            >
              {t('dc.sendHr')}
            </Button>
          </>
        }
      >
        <div className={s.paperForm}>
          <div className={`${s.field} ${s.span2}`}>
            <label htmlFor={`${rid}-k`} className={s.fieldLb}>{t('dc.kind')}</label>
            <select id={`${rid}-k`} key={kind} className={s.select} defaultValue={kind}>
              {PAPER_KINDS.map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div className={s.field}>
            <label htmlFor={`${rid}-f`} className={s.fieldLb}>{t('dc.issued')}</label>
            <input id={`${rid}-f`} className={s.select} value="22/Oct/2026" readOnly />
          </div>
          <div className={s.field}>
            <label htmlFor={`${rid}-t`} className={s.fieldLb}>{t('dc.expires')}</label>
            <input id={`${rid}-t`} className={s.select} value="22/Oct/2028" readOnly />
          </div>
          <div className={`${s.drop} ${s.span2}`}>{t('dc.drop')}</div>
        </div>
      </Modal>
    </div>
  );
}

export function DocsScreen() {
  const m = useMee();
  const t = useT();
  const toast = useToast();
  const [signed, setSigned] = useState(false);
  return (
    <>
      <PageHeader
        title="Hồ sơ & giấy tờ"
        actions={
          m.state === 'ready' ? (
            <>
              <MeeHeaderTools />
              <Button variant="primary" icon={<IconUpload size={18} />} onClick={() => toast({ msg: 'Chưa mở ghi ở bản này' })}>
                {t('dc.addPaper')}
              </Button>
            </>
          ) : undefined
        }
      />
      <StateCard state={m.state}>
        <StateView
          state={m.state}
          {...meeStateProps('docs', m.state, m.retry, () => toast({ msg: 'Đã mở Thêm giấy tờ' }))}
          skeleton={<div className={s.skel}><Skeleton rows={4} /><Skeleton rows={5} /></div>}
        >
          <DocsBody m={m} signed={signed} onSign={() => setSigned(true)} />
        </StateView>
      </StateCard>
    </>
  );
}
